
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { PackageCard } from './PackageCard';
import { FilterSidebar } from './FilterSidebar';
import { useCart } from '../../../hooks/useCart';
import experiencesService from '@/services/experiences';

export interface TravelPackage {
  id: string;
  title: string;
  image: string;
  price: number;
  category: string;
  duration: string;
  location: string;
  description: string;
  rating: number;
  reviews: number;
  itinerary?: Array<{
    day: number;
    title: string;
    description: string;
  }>;
}

export const travelPackages: TravelPackage[] = [];

export const PackagesSection: React.FC = () => {
  const [packages, setPackages] = useState<TravelPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 100000]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data: any = await experiencesService.listExperiences();
        // Just map the raw data to TravelPackage interface
        const mapped = (data || []).map((it: any, idx: number) => ({
          id: it.id || it._id || String(idx + 1),
          title: it.title,
          image: it.imageUrl || '/images/experience-package-2.jpg',
          price: it.price || 0,
          category: it.category || 'General',
          duration: it.duration || '',
          location: it.location || '',
          description: it.description || '',
          rating: it.rating || 0,
          reviews: it.reviews || 0,
          itinerary: it.itinerary || [],
        }));
        if (mounted) setPackages(mapped);
      } catch (e) {
        console.error('Failed to load experiences', e);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const { cartItems, addToCart, removeFromCart, updateQuantity, getCartTotal, getCartItemsCount } = useCart();
  const navigate = useNavigate();

  const items = packages.length ? packages : travelPackages;
  const categories = Array.from(new Set(items.map(pkg => pkg.category)));

  const filteredPackages = useMemo(() => {
    let filtered = items.filter(pkg => {
      const withinPriceRange = pkg.price >= priceRange[0] && pkg.price <= priceRange[1];
      const matchesCategory = !selectedCategory || pkg.category === selectedCategory;
      return withinPriceRange && matchesCategory;
    });

    // No sorting on Experience page (keep original order)

    return filtered;
  }, [priceRange, selectedCategory, items]);

  const handleAddToCart = (pkg: TravelPackage) => {
    const prefixedId = `exp-${pkg.id}`;
    const payload = {
      id: prefixedId,
      title: pkg.title,
      price: pkg.price,
      image: pkg.image,
      category: pkg.category,
      description: pkg.description,
      itinerary: pkg.itinerary
    };

    let newCart: any = [];
    try {
      newCart = addToCart(payload);
      try { window.dispatchEvent(new CustomEvent('cart-updated', { detail: newCart })); } catch (e) { }
    } catch (err) {
      console.error('addToCart failed', err);
    }

    const waitForPresence = (id: string, attempts = 20, interval = 40): Promise<boolean> => {
      return new Promise(resolve => {
        let tries = 0;
        const t = setInterval(() => {
          tries += 1;
          try {
            const saved = localStorage.getItem('travelCart');
            const parsed = saved ? JSON.parse(saved) : [];
            if (Array.isArray(parsed) && parsed.find((it: any) => it.id === id)) {
              clearInterval(t);
              resolve(true);
              return;
            }
          } catch (e) { }
          if (tries >= attempts) {
            clearInterval(t);
            resolve(false);
          }
        }, interval);
      });
    };

    waitForPresence(prefixedId).then(found => {
      if (!found) console.warn('Timed out waiting for cart persistence, navigating anyway');
      navigate(`/experience/details?id=${encodeURIComponent(pkg.id)}&location=${encodeURIComponent(pkg.title)}`);
    });
  };

  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex gap-8">
          {/* Filter Sidebar */}
          <FilterSidebar
            priceRange={priceRange}
            setPriceRange={setPriceRange}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            categories={categories}
          />

          {/* Main Content */}
          <div className="flex-1">
            {/* Header with Sort and Cart */}
            <div className="flex justify-between items-center mb-8">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Travel Packages</h2>
                <p className="text-gray-600">{filteredPackages.length} packages found</p>
              </div>

              <div />
            </div>

            {/* Package Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredPackages.map((pkg) => (
                <PackageCard
                  key={pkg.id}
                  package={pkg}
                  onAddToCart={() => handleAddToCart(pkg)}
                />
              ))}
            </div>

            {filteredPackages.length === 0 && (
              <div className="text-center py-12">
                <i className="ri-search-line text-6xl text-gray-300 mb-4"></i>
                <h3 className="text-xl font-semibold text-gray-600 mb-2">No packages found</h3>
                <p className="text-gray-500">Try adjusting your filters to see more results.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* CartOffcanvas is not shown on Experience page (cart navigates to /cart) */}
    </section>
  );
};
